/**
 * Product Detail Card Component
 * Handles detailed product view with gallery, options, and add-to-cart functionality
 */

import { getColorCode } from '../../utils/color-helpers.js';

// Internal state for product detail card
let currentDetailProduct = null;
let detailSelectedOptions = {};
let detailQuantity = 1;
let currentImageIndex = 0;

/**
 * Reset product detail state
 */
export function resetProductDetailState() {
    currentDetailProduct = null;
    detailSelectedOptions = {};
    detailQuantity = 1;
    currentImageIndex = 0;
}

/**
 * Get current product detail state
 * @returns {Object} Current state
 */
export function getProductDetailState() {
    return {
        product: currentDetailProduct,
        selectedOptions: { ...detailSelectedOptions },
        quantity: detailQuantity,
        imageIndex: currentImageIndex
    };
}

/**
 * Check if a variant option is available for the product
 * @param {Object} product - Product data
 * @param {string} optionName - Option name (e.g., "Size", "Color")
 * @param {string} value - Option value (e.g., "M", "Blue")
 * @returns {boolean} True if option is available
 */
export function isDetailOptionAvailable(product, optionName, value) {
    if (!product?.variants) return true;
    return product.variants.some(v => {
        const optionIndex = product.options?.findIndex(o => o.name === optionName);
        const optionKey = `option${(optionIndex || 0) + 1}`;
        return v[optionKey] === value && v.available !== false;
    });
}

/**
 * Find variant based on selected options
 * @param {Object} product - Product data
 * @param {Object} selectedOptions - Selected option values
 * @returns {Object|null} Matching variant or null
 */
export function findDetailVariant(product = currentDetailProduct, selectedOptions = detailSelectedOptions) {
    if (!product?.variants) return null;

    return product.variants.find(v => {
        return (!selectedOptions.Size || v.option1 === selectedOptions.Size || v.option1 === selectedOptions.size) &&
               (!selectedOptions.Color || v.option2 === selectedOptions.Color || v.option2 === selectedOptions.color) &&
               (!selectedOptions.Style || v.option3 === selectedOptions.Style || v.option3 === selectedOptions.style);
    });
}

/**
 * Navigate gallery to next/previous image
 * @param {Array} images - Array of image URLs
 * @param {number} direction - Direction (-1 for prev, 1 for next)
 */
export function navigateGallery(images, direction) {
    currentImageIndex = (currentImageIndex + direction + images.length) % images.length;
    goToImage(images, currentImageIndex);
}

/**
 * Go to specific image in gallery
 * @param {Array} images - Array of image URLs
 * @param {number} index - Target image index
 */
export function goToImage(images, index) {
    currentImageIndex = index;
    const img = document.getElementById('fyncall-detail-main-image');
    if (img) img.src = images[index];

    const dots = document.querySelectorAll('.fyncall-gallery-dot');
    dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
    });
}

/**
 * Select a detail option (size, color, etc.)
 * @param {string} optionName - Option name
 * @param {string} value - Selected value
 * @param {HTMLElement} element - Clicked element
 */
export function selectDetailOption(optionName, value, element) {
    detailSelectedOptions[optionName] = value;

    // Update UI
    const siblings = element.parentElement.querySelectorAll('.fyncall-option-btn, .fyncall-color-swatch');
    siblings.forEach(s => s.classList.remove('selected'));
    element.classList.add('selected');

    // Update label
    const label = document.getElementById(`selected-${optionName}`);
    if (label) label.textContent = value;

    // Update image if variant has specific image
    if (currentDetailProduct?.variants) {
        const variant = findDetailVariant();
        if (variant?.image_url) {
            const img = document.getElementById('fyncall-detail-main-image');
            if (img) img.src = variant.image_url;
        }
    }
}

/**
 * Update quantity
 * @param {number} delta - Amount to change (-1 or +1)
 * @returns {number} New quantity
 */
export function updateDetailQty(delta) {
    detailQuantity = Math.max(1, detailQuantity + delta);
    const qtyEl = document.getElementById('fyncall-detail-qty');
    if (qtyEl) qtyEl.textContent = detailQuantity;
    return detailQuantity;
}

/**
 * Add product detail item to cart
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onSuccess - Called on success with (product, quantity)
 * @param {Function} callbacks.onError - Called on error with (error)
 * @param {Function} callbacks.onSelectOptions - Called when options need selection
 */
export async function addDetailToCart(callbacks = {}) {
    const product = currentDetailProduct;
    if (!product) return;

    const variant = findDetailVariant();
    const variantId = variant?.shopify_variant_id || variant?.id || product.shopify_variant_id || product.variant_id;

    if (!variantId) {
        callbacks.onSelectOptions?.();
        return;
    }

    const btn = document.getElementById('fyncall-detail-add-btn');
    if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<svg class="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/></svg> Adding...';
    }

    try {
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: detailQuantity })
        });

        if (response.ok) {
            callbacks.onSuccess?.(product, detailQuantity);
        } else {
            throw new Error('Failed to add to cart');
        }
    } catch (error) {
        console.error('[ProductDetail] Add to cart error:', error);
        callbacks.onError?.(error);
    } finally {
        if (btn) {
            btn.disabled = false;
            btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 20c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zM20 20c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add to Cart';
        }
    }
}

/**
 * Handle wishlist action
 * @param {Object} product - Product to add to wishlist
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onSendMessage - Called to send message
 * @param {Function} callbacks.onSuccess - Called on success
 */
export function handleDetailWishlist(product, callbacks = {}) {
    callbacks.onSendMessage?.(`Save ${product.name} to my wishlist`);
    callbacks.onSuccess?.();
}

/**
 * Create product detail card element
 * @param {Object} data - Product data
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onAddToCart - Called when add to cart succeeds
 * @param {Function} callbacks.onWishlist - Called for wishlist action
 * @param {Function} callbacks.onSendMessage - Called to send a message
 * @param {Function} callbacks.onError - Called on errors
 * @param {Function} callbacks.onSelectOptions - Called when options need selection
 * @returns {HTMLElement} Product detail card element
 */
export function createProductDetailCard(data, callbacks = {}) {
    const product = data.product || data;
    const card = document.createElement('div');
    card.className = 'fyncall-product-detail-card';

    // Store product data for later use
    currentDetailProduct = product;
    detailSelectedOptions = {};
    detailQuantity = 1;
    currentImageIndex = 0;

    // Gallery section
    const gallery = document.createElement('div');
    gallery.className = 'fyncall-product-detail-gallery';

    const images = product.images || [product.image_url];

    // Main image
    if (images.length > 0 && images[0]) {
        const img = document.createElement('img');
        img.className = 'fyncall-product-detail-image';
        img.src = images[0];
        img.alt = product.name;
        img.id = 'fyncall-detail-main-image';
        img.onerror = () => {
            gallery.innerHTML = '<div class="fyncall-product-detail-image-placeholder">📦</div>';
        };
        gallery.appendChild(img);

        // Navigation arrows if multiple images
        if (images.length > 1) {
            const prevBtn = document.createElement('button');
            prevBtn.className = 'fyncall-gallery-nav prev';
            prevBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>';
            prevBtn.onclick = () => navigateGallery(images, -1);
            gallery.appendChild(prevBtn);

            const nextBtn = document.createElement('button');
            nextBtn.className = 'fyncall-gallery-nav next';
            nextBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
            nextBtn.onclick = () => navigateGallery(images, 1);
            gallery.appendChild(nextBtn);

            // Dots
            const dots = document.createElement('div');
            dots.className = 'fyncall-gallery-dots';
            dots.id = 'fyncall-gallery-dots';
            images.forEach((_, i) => {
                const dot = document.createElement('span');
                dot.className = `fyncall-gallery-dot${i === 0 ? ' active' : ''}`;
                dot.onclick = () => goToImage(images, i);
                dots.appendChild(dot);
            });
            gallery.appendChild(dots);
        }
    } else {
        gallery.innerHTML = '<div class="fyncall-product-detail-image-placeholder">📦</div>';
    }

    // Badges
    const badges = document.createElement('div');
    badges.className = 'fyncall-product-detail-badges';
    if (product.compare_at_price && product.compare_at_price > product.price) {
        const discount = Math.round((1 - product.price / product.compare_at_price) * 100);
        badges.innerHTML += `<span class="fyncall-product-badge sale">${discount}% OFF</span>`;
    }
    if (product.quantity_available && product.quantity_available <= 5) {
        badges.innerHTML += `<span class="fyncall-product-badge low-stock">Low Stock</span>`;
    }
    gallery.appendChild(badges);

    // Wishlist button
    if (data.show_wishlist !== false) {
        const wishBtn = document.createElement('button');
        wishBtn.className = 'fyncall-wishlist-btn';
        wishBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
        wishBtn.onclick = () => handleDetailWishlist(product, {
            onSendMessage: callbacks.onSendMessage,
            onSuccess: () => callbacks.onWishlist?.(product)
        });
        gallery.appendChild(wishBtn);
    }

    card.appendChild(gallery);

    // Product info
    const info = document.createElement('div');
    info.className = 'fyncall-product-detail-info';

    if (product.brand || product.brand_name) {
        info.innerHTML += `<div class="fyncall-product-detail-brand">${product.brand || product.brand_name}</div>`;
    }
    info.innerHTML += `<div class="fyncall-product-detail-name">${product.name}</div>`;

    // Rating
    if (product.rating) {
        const stars = '★'.repeat(Math.round(product.rating)) + '☆'.repeat(5 - Math.round(product.rating));
        info.innerHTML += `
            <div class="fyncall-product-detail-rating">
                <span class="fyncall-product-detail-stars">${stars}</span>
                <span class="fyncall-product-detail-reviews">(${product.review_count || 0} reviews)</span>
            </div>
        `;
    }

    // Price
    const priceRow = document.createElement('div');
    priceRow.className = 'fyncall-product-detail-price-row';
    priceRow.innerHTML = `<span class="fyncall-product-detail-price">$${parseFloat(product.price).toFixed(2)}</span>`;
    if (product.compare_at_price && product.compare_at_price > product.price) {
        const discount = Math.round((1 - product.price / product.compare_at_price) * 100);
        priceRow.innerHTML += `
            <span class="fyncall-product-detail-compare-price">$${parseFloat(product.compare_at_price).toFixed(2)}</span>
            <span class="fyncall-product-detail-discount">🏷️ ${discount}% OFF</span>
        `;
    }
    info.appendChild(priceRow);

    // Stock indicator
    const stockDiv = document.createElement('div');
    const qty = product.quantity_available || product.quantity || 0;
    const inStock = product.in_stock !== false && qty > 0;
    if (!inStock) {
        stockDiv.className = 'fyncall-stock-indicator out-of-stock';
        stockDiv.innerHTML = '❌ Out of Stock';
    } else if (qty <= 5) {
        stockDiv.className = 'fyncall-stock-indicator low-stock';
        stockDiv.innerHTML = `⚠️ Low Stock - Only ${qty} left!`;
    } else {
        stockDiv.className = 'fyncall-stock-indicator in-stock';
        stockDiv.innerHTML = `✅ In Stock${qty > 50 ? '' : ` (${qty} available)`}`;
    }
    info.appendChild(stockDiv);

    // Options section
    if (product.options && product.options.length > 0) {
        const optionsDiv = document.createElement('div');
        optionsDiv.className = 'fyncall-product-detail-options';
        optionsDiv.id = 'fyncall-detail-options';

        product.options.forEach(option => {
            const group = document.createElement('div');
            group.className = 'fyncall-option-group';

            const label = document.createElement('div');
            label.className = 'fyncall-option-label';
            label.innerHTML = `${option.name}: <span class="fyncall-option-selected" id="selected-${option.name}">Select</span>`;
            group.appendChild(label);

            const isColor = option.name.toLowerCase().includes('color') || option.name.toLowerCase().includes('colour');
            const buttons = document.createElement('div');
            buttons.className = isColor ? 'fyncall-color-options' : 'fyncall-option-buttons';

            option.values.forEach(value => {
                const isAvailable = isDetailOptionAvailable(product, option.name, value);
                if (isColor) {
                    const swatch = document.createElement('button');
                    swatch.className = 'fyncall-color-swatch' + (!isAvailable ? ' unavailable' : '');
                    swatch.style.backgroundColor = getColorCode(value);
                    swatch.title = value;
                    swatch.disabled = !isAvailable;
                    swatch.onclick = () => selectDetailOption(option.name, value, swatch);
                    buttons.appendChild(swatch);
                } else {
                    const btn = document.createElement('button');
                    btn.className = 'fyncall-option-btn';
                    btn.textContent = value;
                    btn.disabled = !isAvailable;
                    btn.onclick = () => selectDetailOption(option.name, value, btn);
                    buttons.appendChild(btn);
                }
            });
            group.appendChild(buttons);
            optionsDiv.appendChild(group);
        });
        info.appendChild(optionsDiv);
    }

    // Quantity row
    const qtyRow = document.createElement('div');
    qtyRow.className = 'fyncall-quantity-row';
    qtyRow.innerHTML = `
        <span class="fyncall-quantity-label">Qty:</span>
        <div class="fyncall-quantity-controls">
            <button class="fyncall-qty-btn" id="fyncall-detail-qty-minus">−</button>
            <span class="fyncall-qty-value" id="fyncall-detail-qty">1</span>
            <button class="fyncall-qty-btn" id="fyncall-detail-qty-plus">+</button>
        </div>
    `;
    info.appendChild(qtyRow);

    card.appendChild(info);

    // Actions
    const actions = document.createElement('div');
    actions.className = 'fyncall-product-detail-actions';
    actions.innerHTML = `
        <button class="fyncall-detail-action-btn primary" id="fyncall-detail-add-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 20c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zM20 20c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
            Add to Cart
        </button>
        ${data.show_size_guide ? '<button class="fyncall-detail-action-btn secondary" id="fyncall-detail-size-guide">📏 Size Guide</button>' : ''}
        ${data.show_shipping_info ? '<button class="fyncall-detail-action-btn secondary" id="fyncall-detail-shipping">📦 Shipping</button>' : ''}
    `;
    card.appendChild(actions);

    // Bind events after card is created
    setTimeout(() => {
        const minusBtn = card.querySelector('#fyncall-detail-qty-minus');
        const plusBtn = card.querySelector('#fyncall-detail-qty-plus');
        const addBtn = card.querySelector('#fyncall-detail-add-btn');
        const sizeGuideBtn = card.querySelector('#fyncall-detail-size-guide');
        const shippingBtn = card.querySelector('#fyncall-detail-shipping');

        if (minusBtn) minusBtn.onclick = () => updateDetailQty(-1);
        if (plusBtn) plusBtn.onclick = () => updateDetailQty(1);
        if (addBtn) {
            addBtn.onclick = () => addDetailToCart({
                onSuccess: (prod, qty) => callbacks.onAddToCart?.(prod, qty),
                onError: (err) => callbacks.onError?.(err),
                onSelectOptions: () => callbacks.onSelectOptions?.()
            });
        }
        if (sizeGuideBtn) sizeGuideBtn.onclick = () => callbacks.onSendMessage?.('Show size guide');
        if (shippingBtn) shippingBtn.onclick = () => callbacks.onSendMessage?.('What are the shipping options?');
    }, 0);

    return card;
}
